import React from "react";
import { Plus, X } from "lucide-react";
import { cn, handleFocus } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ContractRefGroup, ContractRefGroupError } from "./contract-reference-utils";

/**
 * Multi-group editor for contract reference on reown contract types.
 *
 * Renders repeatable pairs of [Contract Reference (text), Ref Qty (float)]
 * laid out in a wrapping grid so multiple groups sit side-by-side,
 * with a "+" button to add more groups and an "x" to remove non-first groups.
 */
export function ReownContractReferenceEditor({
  groups,
  onChange,
  disabled,
  errors,
}: {
  groups: ContractRefGroup[];
  onChange: (groups: ContractRefGroup[]) => void;
  disabled?: boolean;
  /** Per-group field errors, parallel to `groups`. null = no errors. */
  errors: ContractRefGroupError[] | null;
}) {
  const updateGroup = (index: number, field: "ref" | "qty", value: string) => {
    const next = groups.map((g, i) => {
      if (i !== index) return g;
      return { ...g, [field]: value };
    });
    onChange(next);
  };

  const addGroup = () => {
    onChange([...groups, { ref: "", qty: "" }]);
  };

  const removeGroup = (index: number) => {
    if (groups.length <= 1) return;
    const next = groups.filter((_, i) => i !== index);
    onChange(next);
  };

  const hasAnyError = errors !== null;

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div className="flex flex-row items-center gap-4">
        <Label className="font-semibold text-base">Contract References</Label>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 gap-1 text-xs"
            data-testid="reown-add-group"
            onClick={addGroup}
          >
            <Plus className="h-3 w-3" />
            Add
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 w-full" data-testid="reown-ref-groups">
        {groups.map((group, index) => {
          const groupErrors = errors?.[index];
          const refError = groupErrors?.ref;
          const qtyError = groupErrors?.qty;

          return (
            <div
              key={index}
              data-testid={`reown-ref-group-${index}`}
              className="flex items-end gap-1.5 min-w-0"
            >
              {/* Reference */}
              <div className="grid items-center gap-1 w-40">
                {index === 0 && (
                  <Label className="text-xs text-muted-foreground">Reference</Label>
                )}
                <Input
                  type="text"
                  disabled={disabled}
                  placeholder="Reference"
                  value={group.ref}
                  required={false}
                  data-testid={`reown-ref-input-${index}`}
                  data-error={refError ? "true" : undefined}
                  onChange={(e) => updateGroup(index, "ref", e.target.value)}
                  onFocus={handleFocus}
                  className={cn(
                    "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:leading-6",
                    refError
                      ? "ring-red-500 focus:ring-red-500"
                      : "ring-gray-300 focus:ring-indigo-600",
                  )}
                />
              </div>

              {/* Qty */}
              <div className="grid items-center gap-1 w-24">
                {index === 0 && (
                  <Label className="text-xs text-muted-foreground">Qty</Label>
                )}
                <Input
                  type="text"
                  disabled={disabled}
                  placeholder="Qty"
                  value={group.qty}
                  required={false}
                  data-testid={`reown-qty-input-${index}`}
                  data-error={qtyError ? "true" : undefined}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^\-0-9.]+/gi, "");
                    updateGroup(index, "qty", cleaned);
                  }}
                  onFocus={handleFocus}
                  className={cn(
                    "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:leading-6",
                    qtyError
                      ? "ring-red-500 focus:ring-red-500"
                      : "ring-gray-300 focus:ring-indigo-600",
                  )}
                />
              </div>

              {/* Remove button (hidden when only one group remains) */}
              {groups.length > 1 ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeGroup(index)}
                  data-testid={`reown-remove-group-${index}`}
                  className="p-1.5 mb-0.5 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                  aria-label={`Remove reference ${index + 1}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : (
                <div className="w-7" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
